/**
 * Best Trades — the pure math behind Strategy → Best Trades.
 *
 * The question the page answers (Apal 2026-10-03): across the indexes I hold,
 * does every quarter (or year) bring at least one great trade? So:
 *
 * - Every trade shows ONCE, in the period it CLOSED in, at its full gain,
 *   with its days held (Apal 2026-10-03: "any trade when close, we show the
 *   exit"). The days column is what tells a 441-day hold from a quarter's
 *   trade. An earlier rule ("opened this period or last") hid real trades —
 *   a 158-day Nasdaq long that closed on 1 Jul 1996 vanished for touching
 *   three calendar quarters, and the late-90s holds left whole years blank.
 * - An open trade has been marked to the latest price by the backend
 *   (exit_date = latest bar), so it lands in the current period, labelled
 *   open — never given a fake exit.
 * - EVERY period from the first to the last is listed, including the ones
 *   with nothing — a gap must be visible, never skipped.
 * - The headline only counts COMPLETE periods. The running quarter and a
 *   quarter cut by the date range would otherwise count as misses (or hits)
 *   on a fraction of the time.
 *
 * No React here — unit-tested in bestTrades.test.js.
 */

export const GAIN_PRESETS = [5, 10, 15, 20, 30];
export const DEFAULT_MIN_GAIN = 10;
export const TOP_N = 3;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m is 1-based

/** The period a YYYY-MM-DD date falls in. grain: 'quarter' | 'year'. */
export const periodOf = (date, grain) => {
  const y = Number(date.slice(0, 4));
  if (grain === 'year') {
    return { key: `${y}`, year: y, q: null, label: `${y}`, span: '', start: `${y}-01-01`, end: `${y}-12-31` };
  }
  const q = Math.ceil(Number(date.slice(5, 7)) / 3);
  const m0 = (q - 1) * 3 + 1;
  const m1 = m0 + 2;
  return {
    key: `${y}-Q${q}`, year: y, q,
    label: `Q${q} ${y}`,
    span: `${MONTHS[m0 - 1]} – ${MONTHS[m1 - 1]}`,
    start: `${y}-${pad(m0)}-01`,
    end: `${y}-${pad(m1)}-${pad(lastDay(y, m1))}`,
  };
};

const nextPeriod = (p, grain) => {
  if (grain === 'year') return periodOf(`${p.year + 1}-01-01`, grain);
  const [y, q] = p.q === 4 ? [p.year + 1, 1] : [p.year, p.q + 1];
  return periodOf(`${y}-${pad((q - 1) * 3 + 1)}-01`, grain);
};

/** Flatten the signal-analysis response into one list of trades with their index. */
export const flattenTrades = (regions) => {
  const out = [];
  (regions || []).forEach(r => (r.symbols || []).forEach(s => (s.trades || []).forEach(t => {
    out.push({ ...t, symbol: s.symbol, symbol_label: s.label || s.symbol, region: r.region, market: (r.code || r.region || '').toUpperCase() });
  })));
  return out;
};

/** Median, rounded to a whole number; null for no values. */
export const median = (xs) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return Math.round(s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2);
};

// Highest gain first; ties broken by later close, then index name, so the
// order never shuffles between renders.
const byGainDesc = (a, b) =>
  (b.gain - a.gain) || (b.exit_date || '').localeCompare(a.exit_date || '') || a.symbol.localeCompare(b.symbol);

/**
 * Group trades into every period of the window.
 *
 * @param trades    flattenTrades() output
 * @param opts.grain        'quarter' | 'year'
 * @param opts.minGain      a trade "counts" at gain >= minGain (%)
 * @param opts.from, opts.to  optional YYYY-MM-DD range on the CLOSE date
 * @param opts.today        YYYY-MM-DD — periods ending on/after it are in progress
 * @param opts.includeOpen  false drops open trades everywhere
 * @returns { periods (newest first), summary }
 */
export const buildBestTrades = (trades, { grain = 'quarter', minGain = DEFAULT_MIN_GAIN, from = '', to = '', today, includeOpen = true }) => {
  const inRange = (d) => (!from || d >= from) && (!to || d <= to);
  const usable = (trades || []).filter(t =>
    t.gain != null && t.exit_date && (includeOpen || !t.is_open) && inRange(t.exit_date));

  const empty = { periods: [], summary: { complete: 0, hit: 0, pct: null, longestGap: null, avgPerPeriod: null, avgGain: null, medianDays: null, markets: [], indexes: [], hits: 0, trades: 0 } };
  if (!usable.length && !from) return empty;

  const firstDate = from || usable.reduce((m, t) => (t.exit_date < m ? t.exit_date : m), usable[0].exit_date);
  const lastDate = to && to < today ? to : today;
  if (firstDate > lastDate) return empty;

  const buckets = new Map();
  usable.forEach(t => {
    const k = periodOf(t.exit_date, grain).key;
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(t);
  });

  const periods = [];
  for (let p = periodOf(firstDate, grain); p.start <= lastDate; p = nextPeriod(p, grain)) {
    const all = (buckets.get(p.key) || []).slice().sort(byGainDesc);
    const hits = all.filter(t => t.gain >= minGain);
    let status = 'complete';
    if (p.end >= today) status = 'in_progress';
    else if ((from && from > p.start) || (to && to < p.end)) status = 'partial';
    periods.push({ ...p, status, trades: all.length, hits, best: all[0] || null });
  }

  // Headline: complete periods only; longest run of complete periods with no hit.
  const complete = periods.filter(p => p.status === 'complete');
  const hit = complete.filter(p => p.hits.length > 0).length;
  let longestGap = null;
  let run = [];
  const closeRun = () => {
    if (run.length && (!longestGap || run.length > longestGap.length)) {
      longestGap = { length: run.length, from: run[0].label, to: run[run.length - 1].label };
    }
    run = [];
  };
  complete.forEach(p => { if (p.hits.length) closeRun(); else run.push(p); });
  closeRun();

  // Per complete period: how many qualifying trades, and their average gain.
  const completeHits = complete.flatMap(p => p.hits);
  const round1 = (x) => Math.round(x * 10) / 10;

  return {
    periods: periods.reverse(),
    summary: {
      complete: complete.length,
      hit,
      pct: complete.length ? Math.round((hit / complete.length) * 100) : null,
      longestGap,
      avgPerPeriod: complete.length ? round1(completeHits.length / complete.length) : null,
      avgGain: completeHits.length ? round1(completeHits.reduce((a, t) => a + t.gain, 0) / completeHits.length) : null,
      // How long a qualifying trade typically took — the MEDIAN, because a few
      // multi-year holds drag a mean far from the typical trade (2025: one
      // 820-day hold makes the mean 110 days; the typical 10%+ trade took 60).
      medianDays: median(completeHits.map(t => t.days_held).filter(d => d != null)),
      // Which markets the qualifying trades came from, across complete periods
      markets: marketsOf(completeHits).map(m => ({
        ...m,
        pct: Math.round((m.count / completeHits.length) * 100),
        // complete periods in which this market had a qualifying trade
        periodsHit: complete.filter(p => p.hits.some(t => t.market === m.market)).length,
      })),
      // The same per index, for highlighting one index across the years
      // (Apal 2026-10-03: "index should also be allowed to select"). Most
      // quarters won first.
      indexes: indexesOf(completeHits).map(ix => ({
        ...ix,
        periodsHit: complete.filter(p => p.hits.some(t => t.symbol === ix.symbol)).length,
      })).sort((a, b) => (b.periodsHit - a.periodsHit) || (b.count - a.count) || a.label.localeCompare(b.label)),
      hits: periods.reduce((n, p) => n + p.hits.length, 0),
      trades: periods.reduce((n, p) => n + p.trades, 0),
    },
  };
};

/**
 * A year's highlights for its header (Apal 2026-10-03: "show stats near
 * 2026, 2025 … best trade … avg of the top trades" — in place of a separate
 * Year view). Booked trades only: an open trade is in its quarter but not in
 * the year's numbers until it closes. `cells` = overviewRows(...).cells.
 */
export const yearSummary = (cells) => {
  const quarters = (cells || []).filter(Boolean);
  const complete = quarters.filter(p => p.status === 'complete');
  const booked = quarters.flatMap(p => p.hits).filter(t => !t.is_open);
  const best = booked.reduce((b, t) => (!b || t.gain > b.gain ? t : b), null);
  return {
    qHit: complete.filter(p => p.hits.length > 0).length,
    qComplete: complete.length,
    count: booked.length,
    avgGain: booked.length ? Math.round((booked.reduce((a, t) => a + t.gain, 0) / booked.length) * 10) / 10 : null,
    best,
  };
};

/** One entry per index among `hits`: { symbol, label, market, count }. */
const indexesOf = (hits) => {
  const by = new Map();
  hits.forEach(t => {
    const ix = by.get(t.symbol) || { symbol: t.symbol, label: t.symbol_label || t.symbol, market: t.market, count: 0 };
    ix.count += 1;
    by.set(t.symbol, ix);
  });
  return [...by.values()];
};

/**
 * Which markets a period's qualifying trades came from — the tags on each
 * quarter (Apal 2026-10-03: "clearly show what won"). Most trades first,
 * ties broken by the bigger best trade, then name.
 */
export const marketsOf = (hits) => {
  const by = new Map();
  (hits || []).forEach(t => {
    const m = by.get(t.market) || { market: t.market, count: 0, best: -Infinity };
    m.count += 1; m.best = Math.max(m.best, t.gain);
    by.set(t.market, m);
  });
  return [...by.values()].sort((a, b) => (b.count - a.count) || (b.best - a.best) || a.market.localeCompare(b.market));
};

/** Overview grid rows: one per year (newest first), cells per quarter (or one per year). */
export const overviewRows = (periods, grain) => {
  const byYear = new Map();
  periods.forEach(p => {
    if (!byYear.has(p.year)) byYear.set(p.year, grain === 'year' ? [null] : [null, null, null, null]);
    byYear.get(p.year)[grain === 'year' ? 0 : p.q - 1] = p;
  });
  return [...byYear.entries()].sort((a, b) => b[0] - a[0]).map(([year, cells]) => ({ year, cells }));
};
